import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import SearchIcon from '../../assets/icons/svg/SearchIconAdornment.svg'
import { useTranslate } from '@repo/unicity'
import { addressApis } from './Apis'
import { useEffect, useState, useCallback } from 'react'
import { useAtomValue } from 'jotai'
import { languageState } from '@repo/unicity'
import { lowerCase } from 'lodash'
import type { TAddressItem } from '../../types/TAddressItem'
import JPStates from './States.json'
import { AutoComplete } from 'antd'

function useDebounce<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value)

    useEffect(() => {
        const handler = setTimeout(() => setDebounced(value), delay)
        return () => clearTimeout(handler)
    }, [value, delay])

    return debounced
}

async function fetchSearch(keyword: string, signal: AbortSignal) {
    if (keyword.length < 2) {
        return
    }
    const response = await addressApis.findAddress({ keyword, countryCode2Alpha: 'JPN', signal })
    return response
}

type TAddressOption = {
    text: number
    value: string
}

interface SearchAddressProps {
    onAddressSelect: (field: string, value: string) => void
}

const SearchAddress = ({ onAddressSelect }: SearchAddressProps) => {
    const { translate } = useTranslate()
    const [search, setSearch] = useState('')
    const language = useAtomValue(languageState)
    const [address, setAddress] = useState<TAddressOption[]>([])
    const [responseData, setResponseData] = useState<TAddressItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const debouncedSearch = useDebounce(search, 500)
    const [, setSelectedAddress] = useState<TAddressOption>({
        text: 0,
        value: ''
    })

    const makeRenderAddress = useCallback(
        (addresses: TAddressItem[]) => {
            const items: TAddressOption[] = []
            addresses.map((item: TAddressItem) => {
                items.push({
                    text: item.id,
                    value: lowerCase(language) === 'en' ? item.search_address_roman : item.search_address_native
                })
            })
            setAddress(items)
        },
        [language]
    )

    const searchAddress = useCallback(
        async (keyword: string) => {
            if (keyword.length < 2) {
                setAddress([])
                setResponseData([])
                return
            }

            setIsLoading(true)
            try {
                const abortController = new AbortController()
                const data = await fetchSearch(keyword, abortController.signal)
                if (data) {
                    setResponseData(data.data)
                    makeRenderAddress(data.data)
                }
            } catch (error) {
                console.error('Search error:', error)
                setAddress([])
                setResponseData([])
            } finally {
                setIsLoading(false)
            }
        },
        [makeRenderAddress]
    )

    useEffect(() => {
        if (debouncedSearch) {
            searchAddress(debouncedSearch)
        } else {
            setAddress([])
            setResponseData([])
        }
    }, [debouncedSearch, searchAddress])

    const onSelected = (option: TAddressOption) => {
        setSelectedAddress(option)

        const fullData = responseData.find((item: TAddressItem) => item.id === option.text)

        if (fullData) {
            // Use the callback instead of setAttribute
            const prefecture = JPStates['JA' as keyof typeof JPStates].find(item => item === fullData.city_native)

            if (prefecture) {
                onAddressSelect('prefecture', prefecture)
            }
            onAddressSelect('zip', fullData.zip)

            onAddressSelect('district', language === 'en' ? fullData.sub1_roman : fullData.sub1_native)
            onAddressSelect('townNumber', language === 'en' ? fullData.sub2_roman : fullData.sub2_native)
            onAddressSelect('houseNumber', language === 'en' ? fullData.sub3_roman : fullData.sub3_native)
        }
    }

    return (
        <AutoCompleteWrapper>
            <Stack position={'absolute'} left={'12px'} zIndex={2} top={'20%'}>
                <img src={SearchIcon} alt="search" />
            </Stack>
            <AutoComplete
                variant="outlined"
                options={address}
                onSelect={(value: string) => {
                    const option = address.find(item => item.value === value)
                    if (option) {
                        onSelected(option)
                    }
                }}
                onSearch={value => {
                    if (address.length > 0) {
                        setAddress([])
                    }
                    setSearch(value)
                    if (value === '') {
                        setSelectedAddress({
                            text: 0,
                            value: ''
                        })
                    }
                }}
                placeholder={translate('search_address')}
                optionRender={option => {
                    const text = option.data.value.split('　')
                    return (
                        <Stack flexDirection={'row'} gap={'10px'} className="address-option">
                            <Typography fontWeight={700} fontSize={'16px'} lineHeight={'20px'} letterSpacing={'0px'} fontFamily={'Inter'}>
                                {text[0]}
                            </Typography>
                            <Typography fontWeight={400} fontSize={'16px'} lineHeight={'20px'} letterSpacing={'0px'} fontFamily={'Inter'}>
                                {text[1]}
                            </Typography>
                        </Stack>
                    )
                }}
                dropdownAlign={{
                    offset: [0, 10],
                    overflow: {
                        adjustX: 0,
                        adjustY: 1
                    }
                }}
            />
            {isLoading ? (
                <Stack position={'absolute'} right={'20px'} zIndex={2} top={'32%'}>
                    <CircularProgress color="inherit" size={15} sx={{ opacity: 0.4 }} />
                </Stack>
            ) : null}
        </AutoCompleteWrapper>
    )
}

const AutoCompleteWrapper = styled(Stack)(() => ({
    position: 'relative'
}))

export default SearchAddress
